import React, { useEffect, useState } from "react";
import dayjs from "dayjs"; // if you already use it; if not, just remove times
import { getApiClient } from "../../libs/axiosInstance";
import Wrapper from "../_Wrapper";
import { getCurrencySymbol } from "../../libs/countryData";

const axios = getApiClient();

export default function KitchenBoard() {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    loadSessions();

    // optional: auto-refresh every 10-15s
    const intv = setInterval(() => {
      loadSessions(true);
    }, 12000);
    return () => clearInterval(intv);
  }, []);

  async function loadSessions(isBackground = false) {
    if (!isBackground) setLoading(true);
    try {
      const res = await axios.get("/api/myshop/kitchen/sessions");
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error(err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }

  async function changeStatus(sessionId, nextStatus) {
    try {
      await axios.post("/api/myshop/kitchen/set-status", {
        sessionId,
        nextStatus,
      });
      // reload
      loadSessions(true);
    } catch (err) {
      console.error(err);
      alert("Failed to update");
    }
  }

  const ordered = sessions.filter((s) => s.status === "ordered");
  const preparing = sessions.filter((s) => s.status === "preparing");
  const ready = sessions.filter((s) => s.status === "ready_to_serve");

  return (
    <Wrapper>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="m-0">Kitchen</h4>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => loadSessions()}
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <div className="spinner-border" />
          </div>
        ) : (
          <div className="row">
            {/* Ordered */}
            {/* <div className="col-md-6 mb-3">
                  <h6 className="mb-2">New / Ordered ({ordered.length})</h6>
                  <div className="d-flex flex-wrap gap-2">
                  {ordered.length === 0 ? (
                      <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                      No new orders.
                      </div>
                  ) : (
                      ordered.map((s) => (
                      <KitchenCard
                          key={s._id}
                          session={s}
                          onAction={(st) => changeStatus(s._id, st)}
                      />
                      ))
                  )}
                  </div>
              </div> */}

            {/* Preparing */}
            <div className="col-md-6 mb-3">
              <h6 className="mb-2">Preparing ({preparing.length})</h6>
              <div className="d-flex flex-wrap gap-2">
                {preparing.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    None.
                  </div>
                ) : (
                  preparing.map((s) => (
                    <KitchenCard
                      key={s._id}
                      session={s}
                      onAction={(st) => changeStatus(s._id, st)}
                      isPreparing
                    />
                  ))
                )}
              </div>
            </div>

            {/* Ready to serve */}
            <div className="col-md-6 mb-3">
              <h6 className="mb-2">Ready to Serve ({ready.length})</h6>
              <div className="d-flex flex-wrap gap-2">
                {ready.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    No new orders.
                  </div>
                ) : (
                  ready.map((s) => (
                    <KitchenCard
                      key={s._id}
                      session={s}
                      isPreparing={true}
                      onAction={(st) => changeStatus(s._id, st)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

function KitchenCard({ session, onAction, isPreparing = null }) {
  const created = session.createdAt
    ? dayjs(session.createdAt).format("HH:mm")
    : "";

  return (
    <div
      className="card"
      style={{
        width: "100%",
        maxWidth: "360px",
        borderLeft: isPreparing ? "4px solid #0d6efd" : "4px solid #dc3545",
      }}
    >
      <div className="card-body py-2">
        <div className="d-flex justify-content-between">
          <div>
            <div style={{ fontWeight: 600 }}>
              {session.tableLabel || session.tableId} | Order #
              {session.orderRef}
            </div>
            {session.customerName ? (
              <div style={{ fontSize: "0.7rem", color: "#555" }}>
                {session.customerName}
              </div>
            ) : null}
          </div>
          <div style={{ fontSize: "0.65rem", color: "#999" }}>{created}</div>
        </div>

        <div className="mt-2" style={{ maxHeight: "160px", overflowY: "auto" }}>
          {session.items.map((it, idx) => {
            const modsTotal = (it.chosenModifiers || [])
              .flatMap((g) => g.selections || [])
              .reduce((sum, s) => sum + Number(s.priceDelta || 0), 0);
            const perUnit = Number(it.basePrice || 0) + modsTotal;

            return (
              <div key={idx} className="mb-1">
                <div style={{ fontWeight: 500, fontSize: "0.8rem" }}>
                  {it.qty}x {it.name}
                </div>
                {it.chosenModifiers && it.chosenModifiers.length > 0 && (
                  <div style={{ fontSize: "0.65rem", color: "#666" }}>
                    {it.chosenModifiers.map((g, gIdx) => (
                      <div key={gIdx}>
                        <b>{g.groupName}:</b>{" "}
                        {(g.selections || []).map((s) => s.name).join(", ")}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: "0.6rem", color: "#aaa" }}>
                  Unit: {getCurrencySymbol()}
                  {perUnit.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-2 d-flex justify-content-end gap-2">
          {!isPreparing ? (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => onAction("preparing")}
            >
              Start
            </button>
          ) : session.status === "preparing" ? (
            <button
              onClick={() => onAction("ready_to_serve")}
              className="btn btn-sm btn-success"
            >
              Mark ready
            </button>
          ) : (
            session.status === "ready_to_serve" && (
              <></>
              // <button onClick={() => onAction("served")} className="btn btn-sm btn-success">Mark served</button>
            )
          )
          // <button
          //   className="btn btn-sm btn-success"
          //   onClick={() => onAction("served")}
          // >
          //   Mark Served
          // </button>
          }
        </div>
      </div>
    </div>
  );
}
